import { formatName } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { AvatarWithPicture } from "../../AvatarWithPicture";
import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@tellescope/ui";
import { MouseEventHandler } from "react";

interface PersonLike {
  fname?: string;
  lname?: string;
  avatar?: string;
}

interface PersonCellProps<T extends PersonLike> {
  person: T;
  className?: string;
  avatarOnly?: boolean;
  onClick?: MouseEventHandler<HTMLElement>;
}

/**
 * Looks up user by ID and displays their name
 * Shows email if name is not available
 */
export const PersonCell = <T extends PersonLike>({ person, className, avatarOnly, onClick }: PersonCellProps<T>) => {
  const { t } = useTranslation("tickets");

  const displayName = formatName(person) || t("Unknown");

  if (avatarOnly) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                "text-foreground text-sm flex gap-2 items-center hover:opacity-70",
                onClick && "cursor-pointer",
                className
              )}
            >
              <AvatarWithPicture avatarLike={person} className="w-6 h-6" onClick={onClick} />
            </span>
          </TooltipTrigger>
          <TooltipContent side="right">{displayName}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const Component = onClick ? "button" : "span";

  return (
    <Component
      className={cn(
        "text-foreground text-sm flex gap-2 items-center",
        onClick && "cursor-pointer hover:opacity-70",
        className
      )}
      onClick={onClick}
    >
      <AvatarWithPicture avatarLike={person} className="w-6 h-6" fallbackClassName="bg-white" />
      {displayName}
    </Component>
  );
};
